import { useCallback, useEffect, useRef, useState } from 'react'
import candidateChatManager from '../../candidate/CandidateChatManager'
import { applyCoachLoadBalancing } from '../../utils/applyCoachLoadBalancing'
import {
  filterCoachesByStatus,
  getLocaleCompanyFilteredCoaches,
} from '../shared/filterCoaches'
import {
  hasNoAvailableLocaleCoaches,
  logBusyMissedOpportunity,
} from '../shared/logBusyMissedOpportunity'

/** Legacy chat.js coachListEmptyTimer delay before logging busy / no coaches. */
const COACH_LIST_EMPTY_LOG_DELAY_MS = 15000

/**
 * RTC connection + coach occupancy for the floating coach chat modal.
 */
export function useCoachListConnection({
  open,
  socketUrl,
  username,
  avatar,
  chatRoom,
  inHours,
  log_chat_event_path,
  chatCoachInfoPath,
  serverTime,
  userDisplayName,
  userFirstName,
  userLocale,
  userLanguage,
  userCompany,
  userCompanyId,
  partnerCandidate,
  conversationStarter,
  chatMode,
  userId,
  userEmail,
  os,
  buildChatHistoryPath,
  conversationId,
  isMobile,
  clientTime,
  timeOffset,
  deloitteUser,
  userCareerDevelopment,
  opRedesign,
  chatHours,
  hideChatHours,
  modalCopy,
}) {
  const [isConnected, setIsConnected] = useState(false)
  const [coaches, setCoaches] = useState(null)
  const hasLoggedNoCoachesRef = useRef(false)
  const emptyListTimerRef = useRef(null)
  const latestCoachesRef = useRef(null)

  const clearEmptyListTimer = useCallback(() => {
    if (emptyListTimerRef.current) {
      clearTimeout(emptyListTimerRef.current)
      emptyListTimerRef.current = null
    }
  }, [])

  const scheduleBusyMissedOpportunityLog = useCallback(
    (coachList) => {
      if (hasLoggedNoCoachesRef.current || partnerCandidate) {
        clearEmptyListTimer()
        return
      }

      if (
        !hasNoAvailableLocaleCoaches(coachList, {
          userLocale,
          userCompanyId,
          partnerCandidate,
        })
      ) {
        clearEmptyListTimer()
        return
      }

      // Legacy: wait 15s with no free coaches before logging (settle / join delay).
      if (emptyListTimerRef.current) return

      emptyListTimerRef.current = setTimeout(() => {
        emptyListTimerRef.current = null
        if (hasLoggedNoCoachesRef.current || partnerCandidate) return

        const logged = logBusyMissedOpportunity(
          candidateChatManager,
          latestCoachesRef.current || [],
          {
            partnerCandidate,
            userLocale,
            userCompanyId,
            inHours,
          },
        )
        if (logged) hasLoggedNoCoachesRef.current = true
      }, COACH_LIST_EMPTY_LOG_DELAY_MS)
    },
    [partnerCandidate, userCompanyId, userLocale, inHours, clearEmptyListTimer],
  )

  useEffect(() => {
    if (!open) {
      clearEmptyListTimer()

      if (
        candidateChatManager.isConnected() &&
        candidateChatManager.rtcService
      ) {
        const easyrtc = candidateChatManager.rtcService.getEasyRTC()
        const room = candidateChatManager.rtcService.connectionState?.chatRoom

        if (easyrtc && room) {
          const connectionState = candidateChatManager.getConnectionState()
          const hasActiveChat =
            connectionState?.activeCall?.status === 'connected' ||
            connectionState?.chatStatus === 'chat_active'

          if (!hasActiveChat) {
            // CTS-9867: leave room and clear RTC room state so reopen rejoins
            // instead of early-returning from connect() with a stale list.
            const rtcService = candidateChatManager.rtcService
            easyrtc.leaveRoom(room, () => {
              easyrtc.updatePresence('away', 'Coach list closed')
            })
            rtcService.connectionState.chatRoom = null
            rtcService.connectionState.coaches = []
            candidateChatManager.connectionState.chatRoom = null
            candidateChatManager.connectionState.coaches = []
          }
        }
      }

      setIsConnected(false)
      setCoaches(null)
      latestCoachesRef.current = null
      hasLoggedNoCoachesRef.current = false
      return
    }

    hasLoggedNoCoachesRef.current = false
    clearEmptyListTimer()

    if (candidateChatManager.isConnected()) {
      setIsConnected(true)
      // Only seed from cache when still in-room; after modal close chatRoom is
      // cleared and reconnect must rejoin before coaches are valid (CTS-9867).
      const stillInRoom =
        candidateChatManager.rtcService?.connectionState?.chatRoom
      const currentCoaches = candidateChatManager.getCurrentCoaches()
      if (stillInRoom && currentCoaches) {
        setCoaches(currentCoaches)
        latestCoachesRef.current = currentCoaches
        scheduleBusyMissedOpportunityLog(currentCoaches)
      }
    }

    candidateChatManager.forceRefreshRoomOccupants?.()

    const handleConnected = () => {
      setIsConnected(true)
      setTimeout(() => {
        candidateChatManager.forceRefreshRoomOccupants?.()
      }, 1000)

      const refreshInterval = setInterval(() => {
        candidateChatManager.forceRefreshRoomOccupants?.()
      }, 5000)
      window.coachListRefreshInterval = refreshInterval
    }

    const handleCoachesUpdate = (updatedCoaches) => {
      setCoaches(updatedCoaches)
      latestCoachesRef.current = updatedCoaches
      if (updatedCoaches !== null) {
        scheduleBusyMissedOpportunityLog(updatedCoaches)
      }
    }

    const handleDisconnected = () => {
      setIsConnected(false)
      if (window.coachListRefreshInterval) {
        clearInterval(window.coachListRefreshInterval)
        window.coachListRefreshInterval = null
      }
    }

    candidateChatManager.addEventListener('connected', handleConnected)
    candidateChatManager.addEventListener('coachesUpdate', handleCoachesUpdate)
    candidateChatManager.addEventListener('disconnected', handleDisconnected)

    if (socketUrl && username && chatRoom) {
      candidateChatManager
        .connect(socketUrl, username, chatRoom, {
          userMode: 'coach-list',
          socketUrl,
          username,
          chatRoom,
          inHours,
          logChatEventPath: log_chat_event_path,
          chatCoachInfoPath,
          serverTime,
          currentUserLocale: userLocale || 'en',
          displayName: userDisplayName || username,
          userDisplayName: userDisplayName || username,
          firstName: userFirstName || username,
          videoEnabled: false,
          browser: window.navigator?.userAgent || 'unknown',
          userLocale: userLocale || 'en',
          userLanguage: userLanguage || 'English',
          userCompany: userCompany || '',
          userCompanyId: userCompanyId || '',
          partnerCandidate: partnerCandidate || false,
          conversationStarter: conversationStarter || '',
          chatMode: chatMode || 'text',
          userId: userId || null,
          userEmail: userEmail || '',
          avatar: avatar || '',
          os: os || window.navigator?.platform || '',
          buildChatHistoryPath: buildChatHistoryPath || '',
          conversationId: conversationId || '',
          isMobile: isMobile || false,
          clientTime: clientTime || Date.now(),
          timeOffset: timeOffset || 0,
          deloitteUser: deloitteUser || false,
          userCareerDevelopment: userCareerDevelopment || false,
          opRedesign: opRedesign || false,
          enableAudio: false,
          enableVideo: false,
          enableDataChannels: false,
          autoInitUserMedia: false,
          debug: false,
          updateIntervalMs: 5000,
          appName: 'yns.coach_chat',
          presenceMessage: 'Chat List',
          chatHours: chatHours || '',
          hideChatHours: hideChatHours || false,
          modalCopy: modalCopy || {},
        })
        .catch((error) => {
          console.error('CoachChatList - Failed to connect:', error)
        })
    } else {
      console.error('CoachChatList - Missing required connection parameters:', {
        socketUrl: !!socketUrl,
        username: !!username,
        chatRoom: !!chatRoom,
      })
    }

    return () => {
      clearEmptyListTimer()
      if (window.coachListRefreshInterval) {
        clearInterval(window.coachListRefreshInterval)
        window.coachListRefreshInterval = null
      }
      candidateChatManager.removeEventListener('connected', handleConnected)
      candidateChatManager.removeEventListener(
        'coachesUpdate',
        handleCoachesUpdate,
      )
      candidateChatManager.removeEventListener(
        'disconnected',
        handleDisconnected,
      )
    }
  }, [
    open,
    socketUrl,
    username,
    chatRoom,
    inHours,
    log_chat_event_path,
    chatCoachInfoPath,
    serverTime,
    avatar,
    userCareerDevelopment,
    userLocale,
    userDisplayName,
    userFirstName,
    userLanguage,
    userCompany,
    userCompanyId,
    partnerCandidate,
    conversationStarter,
    chatMode,
    userId,
    userEmail,
    os,
    buildChatHistoryPath,
    conversationId,
    isMobile,
    clientTime,
    timeOffset,
    deloitteUser,
    opRedesign,
    chatHours,
    hideChatHours,
    modalCopy,
    scheduleBusyMissedOpportunityLog,
    clearEmptyListTimer,
  ])

  const localeFiltered = getLocaleCompanyFilteredCoaches(coaches, {
    userLocale,
    userCompanyId,
    partnerCandidate,
  })
  const availableCoaches = applyCoachLoadBalancing(
    filterCoachesByStatus(localeFiltered, 'chat'),
  )
  const busyCoaches = filterCoachesByStatus(localeFiltered, 'dnd')

  return { isConnected, coaches, availableCoaches, busyCoaches }
}
